






import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { RenderPostModel } from "@/services/render/types";
import { GrabPostProperties, Param, PostMessage } from "@/services/posts/postTypes/grab";
import IFrame from "@/components/posts/grab/IFrame.vue";
import { setNextItem, UpdateDuration } from "../types";
import { logError } from "@/services/logging/loggingService";

@Component({
    components: {
        IFrame: IFrame
    }
})
export default class grabRender extends Vue {
    @Prop() private item!: RenderPostModel;
    @Prop() private active!: number;
    @Getter("render/getAllowedOrigins") getAllowedOrigins!: string[];

    reload = false;
    reloadComponent = 0;

    // lifecycle hooks
    created() {
        this.setReloadTime();
    }

    mounted() {
        window.addEventListener("message", this.receiveMessage);
    }

    beforeDestroy() {
        window.removeEventListener("message", this.receiveMessage);
    }

    setNextItemTimer = 0;
    @Watch("active", { immediate: true, deep: true })
    onActiveChange(value: number) {
        if (value !== 0 && this.reload) {
            this.reloadIFrame();
        }
        if (value !== 0) {
            const showTimeParam: Param = {
                key: "showTime",
                value: `${this.propertiesModel.showTime * 1000}`
            };
            const postMessage: PostMessage = {
                command: "ShowPost",
                vscIdentifier: this.item.id,
                params: [showTimeParam]
            };
            this.sendMessage(postMessage);

            clearTimeout(this.setNextItemTimer);
            this.setNextItemTimer = setTimeout(
                this.setNextItem,
                this.propertiesModel.showTime * 1000
            );
        }
    }

    get propertiesModel(): GrabPostProperties {
        const props = this.item.properties ? { ...this.item.properties } as GrabPostProperties : {} as GrabPostProperties;

        if (props.url) {
            const a = document.createElement("a");
            a.href = props.url;

            const lowercaseOriginsList = this.getAllowedOrigins.map(str => str.toLowerCase());
            const origin = a.protocol + "//" + a.hostname + (a.port ? ":" + a.port : "");

            if (lowercaseOriginsList.includes(origin.toLowerCase())) {
                const params: { [key: string]: string } = {};

                if (a.search.length > 1) {
                    const parts = a.search.substring(1).split("&");
                    for (let i = 0; i < parts.length; i++) {
                        const kv = parts[i].split("=");
                        const key = decodeURIComponent(kv[0]);
                        params[key] = decodeURIComponent(kv[1] || "");
                    }
                }

                // Add or update vscIdentifier
                params.vscIdentifier = this.item.id;

                const newQuery = Object.keys(params)
                    .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(params[key]))
                    .join("&");

                a.search = "?" + newQuery;
                props.url = a.href;
            }
        }

        return props;
    }

    setNextItem() {
        this.$emit("setNextItem", { id: this.item.id } as setNextItem);
    }

    setReloadTime() {
        if (this.propertiesModel.reloadTime <= 0) return;
        setTimeout(this.initReload, this.propertiesModel.reloadTime * 1000);
    }

    initReload() {
        this.reload = true;
        if (this.active) {
            this.reloadIFrame();
        }
    }

    reloadIFrame() {
        this.reload = false;
        this.setReloadTime();
        this.reloadComponent++;
    }

    async receiveMessage(event: MessageEvent) {
        const lowercaseOriginsList = this.getAllowedOrigins.map(str => str.toLowerCase());
        if (!lowercaseOriginsList.includes(event.origin.toLowerCase())) return;

        const message = event.data as PostMessage;

        switch (message.command.toLowerCase()) {
        case "Register".toLowerCase(): {
            if (this.item.id !== message.vscIdentifier) break;
            const showTimeParam: Param = {
                key: "showTime",
                value: `${this.propertiesModel.showTime * 1000}`
            };
            const postMessage: PostMessage = {
                command: this.active !== 0 ? "ShowPost" : "Duration",
                vscIdentifier: this.item.id,
                params: [showTimeParam]
            };
            this.sendMessage(postMessage);
            break;
        }
        case "NextPost".toLowerCase(): {
            if (this.item.id !== message.vscIdentifier) break;
            clearTimeout(this.setNextItemTimer);
            this.setNextItem();
            break;
        }
        case "SetDuration".toLowerCase(): {
            if (this.item.id !== message.vscIdentifier) break;
            const durationParam = message.params.find(p => p.key.toLowerCase() === "Duration".toLowerCase());
            if (durationParam) {
                const duration = parseInt(durationParam.value, 10);
                clearTimeout(this.setNextItemTimer);
                this.$emit("updateDuration", { id: this.item.id, duration: duration / 1000 } as UpdateDuration);
                this.setNextItemTimer = setTimeout(this.setNextItem, duration);
            }
            break;
        }
        default:
            console.warn("Unknown command received:", message.command);
            break;
        }
    }

    isIFrame = (input: HTMLElement | null): input is HTMLIFrameElement => input !== null && input.tagName === "IFRAME";
    sendMessage(message: PostMessage) {
        try {
            const url = new URL(this.propertiesModel.url);
            const iframe = document.getElementById(`grab-${this.item.id}`);
            if (iframe && this.isIFrame(iframe) && iframe.contentWindow) {
                iframe.contentWindow.postMessage(message, url.origin);
            } else {
                console.log("Iframe not found or not loaded yet");
            }
        } catch (error) {
            // `postMessage` not supported in older IE versions.
            if (!error.message.toLowerCase().includes("Object doesn't support this action".toLowerCase())) {
                logError(`Website Post with id: ${this.item.id}: Send Message has failed: ${error.toString()}`);
            }
        }
    }

    get size() {
        return {
            width: this.propertiesModel.width + "px",
            height: this.propertiesModel.height + "px"
        };
    }
}
